import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  InputBase,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { searchMealPlanTemplates, type MealPlanTemplateSummary } from '@/lib/api/mealPlans';
import { extractErrorMessage } from '@/utils/toast-utils';
import { MealPlanRestrictionBadges } from '@/components/shared/MealPlanRestrictionBadges';
import clipboardAccentIcon from '@/assets/images/clipboard-accent.svg';
import copyStackAccentIcon from '@/assets/images/copy-stack-accent.svg';
import documentEditAccentIcon from '@/assets/images/document-edit-accent.svg';

export type AddDayCreationMode = 'blank' | 'import' | 'copy';

export type AddDayPayload = {
  dayName: string;
  mode: AddDayCreationMode;
  copyFromId?: string;
  templateId?: string;
  template?: MealPlanTemplateSummary;
};

export interface ClientDetailsV2AddDayDialogProps {
  open: boolean;
  onClose: () => void;
  defaultDayName: string;
  copyFromOptions?: Array<{ id: string; name: string; detail?: string }>;
  onSubmit?: (payload: AddDayPayload) => void;
}

const modeOptions: Array<{
  key: AddDayCreationMode;
  title: string;
  subtitle: string;
  iconSrc: string;
}> = [
  {
    key: 'blank',
    title: 'Blank Day',
    subtitle: 'Start from scratch',
    iconSrc: documentEditAccentIcon,
  },
  {
    key: 'import',
    title: 'Import Template',
    subtitle: 'Use an existing template',
    iconSrc: clipboardAccentIcon,
  },
  {
    key: 'copy',
    title: 'Copy Existing',
    subtitle: 'Duplicate a day',
    iconSrc: copyStackAccentIcon,
  },
];

export const ClientDetailsV2AddDayDialog: React.FC<ClientDetailsV2AddDayDialogProps> = ({
  open,
  onClose,
  defaultDayName,
  copyFromOptions,
  onSubmit,
}) => {
  const formatKcal = React.useCallback((value: number | undefined) => {
    if (!Number.isFinite(value)) return '';
    return `${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} kcal`;
  }, []);

  const [dayName, setDayName] = React.useState(defaultDayName);
  const [selectedMode, setSelectedMode] = React.useState<AddDayCreationMode>('blank');
  const [templateSearch, setTemplateSearch] = React.useState('');
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string>('');
  const [selectedCopyFromId, setSelectedCopyFromId] = React.useState<string>(copyFromOptions?.[0]?.id || '');
  const [templateOptions, setTemplateOptions] = React.useState<MealPlanTemplateSummary[]>([]);
  const [isTemplateLoading, setIsTemplateLoading] = React.useState(false);
  const [templateLoadError, setTemplateLoadError] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setDayName(defaultDayName);
    setSelectedMode('blank');
    setTemplateSearch('');
    setSelectedTemplateId('');
    setSelectedCopyFromId(copyFromOptions?.[0]?.id || '');
  }, [open, defaultDayName, copyFromOptions]);

  React.useEffect(() => {
    if (!open || selectedMode !== 'import') return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIsTemplateLoading(true);
      setTemplateLoadError('');
      try {
        const response = await searchMealPlanTemplates({
          search: templateSearch || undefined,
          page: 1,
          pageSize: 100,
        });
        if (cancelled) return;
        if (!response.success) {
          throw new Error(response.message || 'Unable to load templates');
        }
        const templates = response.data?.templates ?? [];
        setTemplateOptions(templates);
        setSelectedTemplateId((prev) => {
          if (prev && templates.some((item) => item.id === prev)) return prev;
          return templates[0]?.id ?? '';
        });
      } catch (error) {
        if (cancelled) return;
        setTemplateOptions([]);
        setSelectedTemplateId('');
        setTemplateLoadError(extractErrorMessage(error) || 'Unable to load templates');
      } finally {
        if (!cancelled) setIsTemplateLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, selectedMode, templateSearch]);

  const shouldShowTemplatePicker = selectedMode !== 'blank';
  const templateSearchPlaceholder = selectedMode === 'copy' ? 'Search Existing Day' : 'Search Template';
  const existingCopyOptions = copyFromOptions || [];
  const parsePickerDetail = React.useCallback((detail: string | undefined) => {
    const normalized = (detail || '').trim();
    if (!normalized) return { kcalLabel: '', metaLabel: '' };

    const match = normalized.match(/^(\d+(?:\.\d+)?)\s*kcal\s*·\s*(.*)$/i);
    if (!match) return { kcalLabel: '', metaLabel: normalized };

    const kcalLabel = `${match[1]} kcal`;
    const metaLabel = (match[2] || '').trim();
    return { kcalLabel, metaLabel };
  }, []);

  const activePickerOptions = React.useMemo(() => {
    if (selectedMode === 'copy') {
      return existingCopyOptions.map((opt) => {
        const parsed = parsePickerDetail(opt.detail);
        return {
          id: opt.id,
          name: opt.name,
          kcalLabel: parsed.kcalLabel,
          metaLabel: parsed.metaLabel,
        };
      });
    }

    return templateOptions.map((template) => ({
      id: template.id,
      name: template.templateName?.trim() || template.name?.trim() || 'Untitled template',
      kcalLabel: formatKcal(template.calories),
      metaLabel:
        Number.isFinite(template.protein) || Number.isFinite(template.carbs) || Number.isFinite(template.fat)
          ? `P:${Number(template.protein ?? 0).toFixed(1)}g C:${Number(template.carbs ?? 0).toFixed(1)}g F:${Number(template.fat ?? 0).toFixed(1)}g`
          : '',
      template,
    }));
  }, [existingCopyOptions, formatKcal, parsePickerDetail, selectedMode, templateOptions]);

  const filteredTemplates = React.useMemo(() => {
    const needle = templateSearch.trim().toLowerCase();
    return activePickerOptions.filter((option) => {
      if (!needle) return true;
      return option.name.toLowerCase().includes(needle);
    });
  }, [activePickerOptions, templateSearch]);

  const handleSubmit = React.useCallback(() => {
    const normalizedDayName = dayName.trim() || defaultDayName;
    const selectedTemplate = selectedMode === 'import'
      ? templateOptions.find((template) => template.id === selectedTemplateId)
      : undefined;

    onSubmit?.({
      dayName: normalizedDayName,
      mode: selectedMode,
      copyFromId: selectedMode === 'copy' ? selectedCopyFromId : undefined,
      templateId: selectedMode === 'import' ? selectedTemplateId : undefined,
      template: selectedTemplate,
    });

    onClose();
  }, [dayName, defaultDayName, onClose, onSubmit, selectedCopyFromId, selectedMode, selectedTemplateId, templateOptions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 24px)', sm: 'min(92vw, 560px)' },
          maxWidth: 560,
          borderRadius: '16px',
          bgcolor: '#fff',
          m: { xs: 1.5, sm: 2 },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)' },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2.2, sm: 2.6 }, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.2 }}>
          <Box>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.75rem', fontWeight: 600, color: '#111827', lineHeight: 1.1 }}>
              Add New Day
            </Typography>
            <Typography sx={{ mt: 0.35, fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#6b7280' }}>
              Configure a new day for the meal plan
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              width: 28,
              height: 28,
              border: '1px solid #d0d5dd',
              color: '#98a2b3',
              '&:hover': { bgcolor: '#f8fafc' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2.2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.2 }}>
          <Box>
            <Typography sx={{ mb: 0.55, fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#98a2b3' }}>
              Day Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={dayName}
              onChange={(event) => setDayName(event.target.value)}
              placeholder="Day Name"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  height: 36,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.84rem',
                  '& fieldset': { borderColor: '#d7dce3' },
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 1.9, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
          {modeOptions.map((mode) => {
            const selected = selectedMode === mode.key;
            return (
              <Button
                key={mode.key}
                onClick={() => setSelectedMode(mode.key)}
                sx={{
                  minHeight: 96,
                  borderRadius: '8px',
                  border: selected ? '1.5px solid #2aabd7' : '1px solid #d9e2ea',
                  bgcolor: selected ? '#eef9fd' : '#fff',
                  textTransform: 'none',
                  p: 1.1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.4,
                  '&:hover': { bgcolor: selected ? '#eef9fd' : '#f8fbfd' },
                }}
              >
                <Box
                  component="img"
                  src={mode.iconSrc}
                  alt={mode.title}
                  sx={{
                    width: 22,
                    height: 22,
                    objectFit: 'contain',
                  }}
                />
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 600, color: '#344054', lineHeight: 1.1 }}>
                  {mode.title}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3', lineHeight: 1.1 }}>
                  {mode.subtitle}
                </Typography>
              </Button>
            );
          })}
        </Box>

        {shouldShowTemplatePicker ? (
          <>
            <Box
              sx={{
                mt: 1.9,
                border: '1px solid #dbe3eb',
                borderRadius: '10px',
                height: 40,
                px: 1.1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.9,
                bgcolor: '#f7f9fc',
              }}
            >
              <SearchRoundedIcon sx={{ fontSize: 18, color: '#9aa8b8' }} />
              <InputBase
                value={templateSearch}
                onChange={(event) => setTemplateSearch(event.target.value)}
                placeholder={templateSearchPlaceholder}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    p: 0,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.92rem',
                    color: '#667085',
                  },
                }}
              />
            </Box>

            <Typography sx={{ mt: 1.8, fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 700, color: '#1f2937' }}>
              {selectedMode === 'copy' ? 'Choose From Existing Day' : 'Choose From Existing Template'}
            </Typography>

            <Box sx={{ mt: 0.9, border: '1px solid #e0e7ef', borderRadius: '10px', overflow: 'hidden', bgcolor: '#fff' }}>
              <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                {selectedMode === 'import' && isTemplateLoading ? (
                  <Box sx={{ px: 1.1, py: 1.1 }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.84rem', color: '#98a2b3' }}>
                      Loading templates...
                    </Typography>
                  </Box>
                ) : selectedMode === 'import' && templateLoadError ? (
                  <Box sx={{ px: 1.1, py: 1.1 }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.84rem', color: '#b42318' }}>
                      {templateLoadError}
                    </Typography>
                  </Box>
                ) : filteredTemplates.length ? (
                  filteredTemplates.map((template, index) => {
                    const selectedTemplate = selectedMode === 'copy' ? selectedCopyFromId === template.id : selectedTemplateId === template.id;
                    return (
                      <Box
                        key={template.id}
                        onClick={() => {
                          if (selectedMode === 'copy') setSelectedCopyFromId(template.id);
                          else setSelectedTemplateId(template.id);
                        }}
                        sx={{
                          px: 1.1,
                          py: 1.0,
                          minHeight: 56,
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) 84px minmax(0, 190px) 22px',
                          columnGap: 1.1,
                          alignItems: 'center',
                          borderBottom: index === filteredTemplates.length - 1 ? 'none' : '1px solid #ecf1f6',
                          bgcolor: selectedTemplate ? '#f6fbff' : '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.55 }}>
                          <Typography
                            sx={{
                              minWidth: 0,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.84rem',
                              fontWeight: 500,
                              color: '#475467',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {template.name}
                          </Typography>
                          {'template' in template && template.template ? (
                            <MealPlanRestrictionBadges source={template.template} maxVisible={2} />
                          ) : null}
                        </Box>
                        <Typography
                          sx={{
                            minWidth: 0,
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.84rem',
                            fontWeight: 700,
                            color: '#111827',
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {template.kcalLabel || ''}
                        </Typography>

                        <Typography
                          sx={{
                            minWidth: 0,
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                            color: '#98a2b3',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {template.metaLabel || ''}
                        </Typography>

                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            border: selectedTemplate ? '2px solid #20aedc' : '2px solid #e1e7ef',
                            display: 'grid',
                            placeItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {selectedTemplate ? (
                            <Box
                              sx={{
                                width: 11,
                                height: 11,
                                borderRadius: '50%',
                                bgcolor: '#20aedc',
                              }}
                            />
                          ) : null}
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ px: 1.1, py: 1.1 }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.84rem', color: '#98a2b3' }}>
                      No templates found
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        ) : null}

        <Box sx={{ mt: 1.8, borderTop: '1px solid #ecf1f6' }} />

        <Box sx={{ mt: 2.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#9aa3af',
              px: 0.4,
              '&:hover': { bgcolor: 'transparent', color: '#8893a0' },
            }}
          >
            BACK
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            endIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              minHeight: 34,
              minWidth: 120,
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.78rem',
              fontWeight: 600,
              bgcolor: '#2aabd7',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#249cc5', boxShadow: 'none' },
            }}
          >
            Add Day
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
